import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Attachment } from 'src/app/shared/interfaces/psa-project-register';
import { AppModalService } from 'src/app/shared/services/app-modal.service';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { ProjectTable } from '../project-table/project-table.component';
import { Subscription } from 'rxjs';
import { ModalConfirmationComponent } from '../modal-confirmation/modal-confirmation.component';
import { normalizeExternalLink } from 'src/app/helpers/utils/link.util';

@Component({
  standalone: false,
  selector: 'app-psa-add-others-links-documents',
  templateUrl: './psa-add-others-links-documents.component.html',
  styleUrls: ['./psa-add-others-links-documents.component.scss'],
})
export class AddOthersLinksDocumentsComponent extends ProjectTable<Attachment> implements OnInit, OnDestroy {
  private static readonly PAGE_SIZE = 5;

  @Input() formRegister!: FormGroup;
  @Output() attachmentsChanged = new EventEmitter<Attachment[]>();

  attachmentsAdded: Attachment[] = [];
  attachmentsSubscription?: Subscription;

  constructor(
    private modalService: AppModalService,
    private readonly toastService: AppToastService,
  ) {
    super();
    this.pagination.page = 1;
    this.pagination.size = AddOthersLinksDocumentsComponent.PAGE_SIZE;
  }

  ngOnInit(): void {
    const initialAttachments = this.formRegister.get('attachments')?.value;
    if (initialAttachments && Array.isArray(initialAttachments)) {
      this.attachmentsAdded = initialAttachments;
      this.updateTableDataOthersLinksDocuments();
    }

    this.attachmentsSubscription = this.formRegister.get('attachments')?.valueChanges.subscribe((value: Attachment[]) => {
      if (value && Array.isArray(value)) {
        this.attachmentsAdded = value;
        this.updateTableDataOthersLinksDocuments();
      }
    });
  }

  ngOnDestroy(): void {
    this.attachmentsSubscription?.unsubscribe();
  }

  get paginatedAttachments(): Attachment[] {
    const page = this.pagination.page ?? 1;
    const size = this.pagination.size ?? AddOthersLinksDocumentsComponent.PAGE_SIZE;
    const start = (page - 1) * size;
    return this.attachmentsAdded.slice(start, start + size);
  }

  get currentPage(): number {
    return this.pagination.page ?? 1;
  }

  trackByAttachment(_index: number, attachment: Attachment): string {
    return attachment.link ?? '';
  }

  validateDisabledButton(): boolean {
    const raw = this.formRegister.get('relevantDocumentationAdd')?.value || '';
    return !this.isValidURL(normalizeExternalLink(raw));
  }

  addAttachment(): void {
    const raw = this.formRegister.get('relevantDocumentationAdd')?.value || '';
    const normalizedLink = normalizeExternalLink(raw);

    if (!this.isValidURL(normalizedLink)) {
      this.treatHarvestAddError();
      return;
    }

    const attachment: Attachment = {
      link: normalizedLink,
      type: 'OTHER_METHODOLOGY',
    };

    const alreadyExists = this.attachmentsAdded.some(
      item => normalizeExternalLink(item.link) === normalizedLink,
    );

    if (alreadyExists) {
      this.treatDuplicateLinkError();
      return;
    }

    this.attachmentsAdded.push(attachment);
    this.updateTableDataOthersLinksDocuments();
    this.formRegister.get('attachments')?.setValue([...this.attachmentsAdded]);
    this.attachmentsChanged.emit(this.attachmentsAdded);
    this.toastService.show({
      variant: 'success',
      label: 'Documento vinculado com sucesso!',
      description: normalizedLink,
    });
    this.clearInputs();
  }

  deleteAttachment(attachment: Attachment): void {
    const targetLink = normalizeExternalLink(attachment.link);
    const index = this.attachmentsAdded.findIndex(
      item => normalizeExternalLink(item.link) === targetLink,
    );

    if (index === -1) {
      return;
    }

    const [removed] = this.attachmentsAdded.splice(index, 1);
    this.updateTableDataOthersLinksDocuments();
    this.formRegister.get('attachments')?.setValue([...this.attachmentsAdded]);
    this.attachmentsChanged.emit(this.attachmentsAdded);
    this.toastService.show({
      variant: 'warning',
      label: 'Documento desvinculado!',
      description: normalizeExternalLink(removed.link) || removed.link || '',
    });
  }

  onPageChange(currentPage: number): void {
    this.pagination.page = currentPage;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.onPageChange(page);
  }

  private isValidURL(url: string): boolean {
    if (!url) return false;
    try {
      const u = new URL(url);
      return !!u.hostname && u.hostname.includes('.');
    } catch {
      return false;
    }
  }

  private clearInputs(): void {
    this.formRegister.get('relevantDocumentationAdd')?.setValue('');
  }

  private updateTableDataOthersLinksDocuments(): void {
    this.itens = [...this.attachmentsAdded];
    this.totalItens = this.attachmentsAdded.length || 0;
    this.handleTotalPages({ pageSize: this.pagination.size ?? AddOthersLinksDocumentsComponent.PAGE_SIZE });

    if ((this.pagination.page ?? 1) > this.totalPages) {
      this.pagination.page = this.totalPages || 1;
    }
  }

  private treatHarvestAddError(): void {
    this.modalService.open(ModalConfirmationComponent, {
      modalTitleType: 'error',
      modalType: 'info',
      title: 'Erro',
      content: 'Link Inválido!',
      closeButton: true,
    });
  }

  private treatDuplicateLinkError(): void {
    this.modalService.open(ModalConfirmationComponent, {
      modalTitleType: 'error',
      modalType: 'info-ok',
      title: 'Atenção',
      content: 'Este link já foi incluído na lista.',
      closeButton: true,
    });
  }
}
